import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar, Copy, Check, Smartphone, Laptop, Mail, Trash2 } from 'lucide-react';
import { apiClient } from '../utils/api';

interface CalendarSubscriptionProps {
  className?: string;
}

interface FeedStatus {
  has_feed: boolean;
  feed_url: string | null;
  webcal_url: string | null;
  feed_type: string | null;
  created_at: string | null;
  event_counts: Record<string, number>;
  available_types: string[];
}

const TYPE_LABELS: Record<string, { label: string; description: string }> = {
  family: { label: 'Family events', description: 'Birthdays, appointments, family activities' },
  custody: { label: 'Custody schedule', description: 'Custody days and handoffs' },
  chores: { label: 'Chore assignments', description: 'Posted chore tasks' },
  school: { label: 'School calendar', description: 'Holidays, early releases, school events' },
};

function parseSelectedTypes(feedType: string | null, available: string[]): Set<string> {
  if (!feedType || feedType === 'full') return new Set(available);
  if (feedType === 'custody_only') return new Set(['custody']);
  if (feedType === 'chores_only') return new Set(['chores']);
  if (feedType === 'school_only') return new Set(['school']);
  return new Set(feedType.split(',').map(t => t.trim()));
}

export const CalendarSubscription: React.FC<CalendarSubscriptionProps> = ({ className = '' }) => {
  const [feedStatus, setFeedStatus] = useState<FeedStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadFeedStatus();
  }, []);

  const loadFeedStatus = async () => {
    try {
      const data = await apiClient.get('/api/calendar-feed/status');
      setFeedStatus(data);
      const available = data.available_types || [];
      setSelectedTypes(parseSelectedTypes(data.feed_type, available));
    } catch (err) {
      console.error('Failed to load feed status:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateFeed = useCallback(async (types: Set<string>) => {
    const feedType = Array.from(types).join(',');
    if (!feedType) return;
    setGenerating(true);
    try {
      const data = await apiClient.post('/api/calendar-feed/generate', { feed_type: feedType });
      setFeedStatus(prev => prev ? {
        ...prev,
        has_feed: true,
        feed_url: data.feed_url,
        webcal_url: data.webcal_url,
        feed_type: data.feed_type,
        created_at: data.created_at,
      } : prev);
    } catch (err) {
      console.error('Failed to generate feed:', err);
    } finally {
      setGenerating(false);
    }
  }, []);

  const toggleType = useCallback((type: string) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }

      // Debounced regeneration if feed is active
      if (feedStatus?.has_feed && next.size > 0) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => generateFeed(next), 500);
      }

      return next;
    });
  }, [feedStatus?.has_feed, generateFeed]);

  const revokeFeed = async () => {
    try {
      await apiClient.delete('/api/calendar-feed');
      setFeedStatus(prev => prev ? {
        ...prev,
        has_feed: false,
        feed_url: null,
        webcal_url: null,
        feed_type: null,
        created_at: null,
      } : prev);
    } catch (err) {
      console.error('Failed to revoke feed:', err);
    }
  };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const instructions = [
    {
      platform: 'iPhone/iPad',
      icon: <Smartphone className="w-5 h-5" />,
      steps: [
        'Copy the calendar URL',
        'Open Settings, then Calendar',
        'Tap "Accounts", then "Add Account"',
        'Choose "Other", then "Add Subscribed Calendar"',
        'Paste the URL and tap "Next"',
      ]
    },
    {
      platform: 'Google Calendar',
      icon: <Laptop className="w-5 h-5" />,
      steps: [
        'Copy the calendar URL',
        'Open Google Calendar on computer',
        'Click + next to "Other calendars"',
        'Select "From URL"',
        'Paste the URL and click "Add calendar"',
      ]
    },
    {
      platform: 'Outlook',
      icon: <Mail className="w-5 h-5" />,
      steps: [
        'Copy the calendar URL',
        'Open Outlook Calendar',
        'Click "Add calendar", then "From internet"',
        'Paste the URL',
        'Name the calendar and click "Import"',
      ]
    }
  ];

  const available = feedStatus?.available_types || [];
  const counts = feedStatus?.event_counts || {};
  const noTypesSelected = selectedTypes.size === 0;

  const renderCheckboxes = () => (
    <div className="mb-4">
      <label className="text-sm text-gray-500 block mb-2">Include in feed:</label>
      <div className="space-y-2">
        {available.map((type) => {
          const info = TYPE_LABELS[type];
          if (!info) return null;
          const count = counts[type] ?? 0;
          const checked = selectedTypes.has(type);

          return (
            <label
              key={type}
              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                checked
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleType(type)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <div>
                  <div className="font-medium text-sm text-gray-900">{info.label}</div>
                  <div className="text-xs text-gray-500">{info.description}</div>
                </div>
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap ml-3">
                {count} {count === 1 ? 'event' : 'events'}
              </span>
            </label>
          );
        })}
      </div>
      {noTypesSelected && (
        <p className="text-sm text-amber-600 mt-2">Select at least one event type to generate a feed.</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl p-6 shadow-md border border-gray-200 ${className}`}>
        <h2 className="text-2xl font-semibold mb-6 flex items-center text-gray-900">
          <Calendar className="mr-3" />
          Calendar Subscriptions
        </h2>
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl p-6 shadow-md border border-gray-200 ${className}`}>
      <h2 className="text-2xl font-semibold mb-6 flex items-center text-gray-900">
        <Calendar className="mr-3" />
        Calendar Subscriptions
      </h2>

      {feedStatus?.has_feed ? (
        <div className="mb-8">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-3">
            <label className="text-sm text-gray-500 block mb-2">Feed URL (copy this)</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={feedStatus.feed_url || ''}
                readOnly
                className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 font-mono"
                onClick={(e) => e.currentTarget.select()}
              />
              <button
                onClick={() => feedStatus.feed_url && copyToClipboard(feedStatus.feed_url)}
                className={`px-3 py-2 rounded-lg transition-all ${
                  copiedUrl ? 'bg-green-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                {copiedUrl ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {renderCheckboxes()}

          {generating && (
            <p className="text-sm text-blue-500 mb-3">Updating feed...</p>
          )}

          <div className="flex gap-3">
            <button
              onClick={revokeFeed}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg text-sm text-red-600"
            >
              <Trash2 className="w-4 h-4" /> Revoke Feed
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-8">
          <p className="text-gray-600 mb-4">
            Subscribe to your HousePoints calendar from Apple Calendar, Google Calendar, Outlook, or any app that supports iCal feeds.
          </p>

          {renderCheckboxes()}

          <button
            onClick={() => generateFeed(selectedTypes)}
            disabled={generating || noTypesSelected}
            className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg font-medium"
          >
            {generating ? 'Generating...' : 'Generate Calendar Feed'}
          </button>
        </div>
      )}

      {/* Setup Instructions */}
      <div>
        <h3 className="text-lg font-medium mb-4 text-gray-900">Setup Instructions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {instructions.map((instruction) => (
            <div key={instruction.platform} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium flex items-center mb-3">
                {instruction.icon}
                <span className="ml-2">{instruction.platform}</span>
              </h4>
              <ol className="space-y-2 text-sm text-gray-600">
                {instruction.steps.map((step, index) => (
                  <li key={index} className="flex">
                    <span className="text-blue-400 mr-2">{index + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
